import React, { useState } from "react";
import {
  Edit,
  Trash2,
  Eye,
  MoreVertical,
  Phone,
  Mail,
  User
} from "lucide-react";
import { format } from "date-fns";

const PatientTable = ({
  patients,
  onEdit,
  onDelete,
  onView,
  selectedPatients = [],
  onSelectPatient,
  onSelectAll
}) => {
  const [expandedRow, setExpandedRow] = useState(null);

  const getStatusBadge = (patient) => {
    const status = patient.status || "active";

    const config = {
      active: "bg-green-100 text-green-700",
      inactive: "bg-gray-100 text-gray-600",
      suspended: "bg-red-100 text-red-700"
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config[status]}`}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          
          {/* HEADER */}

          <thead className="bg-gray-50 border-b">
            <tr>

              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={
                    selectedPatients.length === patients.length &&
                    patients.length > 0
                  }
                  onChange={onSelectAll}
                  className="rounded border-gray-300"
                />
              </th>

              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Patient
              </th>

              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Contact
              </th>

              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Status
              </th>

              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>

            </tr>
          </thead>

          {/* BODY */}

          <tbody className="bg-white divide-y divide-gray-200">

            {patients.map((patient) => (

              <React.Fragment key={patient.id}>

                <tr
                  className={`group hover:bg-gray-50 transition ${
                    expandedRow === patient.id ? "bg-blue-50" : ""
                  }`}
                >

                  {/* SELECT */}

                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedPatients.includes(patient.id)}
                      onChange={() => onSelectPatient(patient.id)}
                      className="rounded border-gray-300"
                    />
                  </td>

                  {/* PATIENT INFO */}

                  <td className="px-6 py-4">

                    <div className="flex items-center">

                      {/* Avatar */}

                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold text-sm">
                        {patient.first_name?.charAt(0)}
                        {patient.last_name?.charAt(0)}
                      </div>

                      {/* Info */}

                      <div className="ml-4">

                        <div className="text-sm font-semibold text-gray-900">
                          {patient.first_name} {patient.last_name}
                        </div>

                        <div className="text-xs text-gray-500">
                          ID: {patient.patient_code || "N/A"}
                        </div>

                        {patient.position?.title && (
                          <div className="text-xs text-gray-400">
                            {patient.position.title}
                          </div>
                        )}

                      </div>

                    </div>

                  </td>

                  {/* CONTACT */}

                  <td className="px-6 py-4">

                    <div className="space-y-1">

                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Phone className="h-4 w-4 text-gray-400" />
                        {patient.mobile || "N/A"}
                      </div>

                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Mail className="h-4 w-4 text-gray-400" />
                        {patient.email || "N/A"}
                      </div>

                    </div>

                  </td>

                  {/* STATUS */}

                  <td className="px-6 py-4">
                    {getStatusBadge(patient)}
                  </td>

                  {/* ACTIONS */}

                  <td className="px-6 py-4 text-right">

                    <div className="flex items-center justify-end space-x-2">

                      <button
                        onClick={() => onView(patient)}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-primary-600 transition"
                        title="View"
                      >
                        <Eye className="h-4 w-4" />
                      </button>

                      <button
                        onClick={() => onEdit(patient)}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>

                      <button
                        onClick={() => onDelete(patient)}
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-red-600 transition"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>

                      <button
                        onClick={() =>
                          setExpandedRow(
                            expandedRow === patient.id ? null : patient.id
                          )
                        }
                        className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>

                    </div>

                  </td>

                </tr>

                {/* EXPANDED ROW */}

                {expandedRow === patient.id && (

                  <tr className="bg-gray-50">

                    <td colSpan="5" className="px-6 py-5">

                      <div className="grid grid-cols-3 gap-6">

                        {/* PERSONAL */}

                        <div className="bg-white rounded-lg border p-4 shadow-sm">

                          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">
                            Personal Info
                          </h4>

                          <p className="text-sm text-gray-700">
                            Gender: {patient.gender || "N/A"}
                          </p>

                          <p className="text-sm text-gray-700">
                            DOB: {patient.dob
                              ? format(new Date(patient.dob), "dd MMM yyyy")
                              : "N/A"}
                          </p>

                        </div>

                        {/* WORK */}

                        <div className="bg-white rounded-lg border p-4 shadow-sm">

                          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">
                            Work Info
                          </h4>

                          <p className="text-sm text-gray-700">
                            Employee Id: {patient.employee_code || "N/A"}
                          </p>

                          <p className="text-sm text-gray-700">
                            Department: {patient.department?.name || "N/A"}
                          </p>

                          <p className="text-sm text-gray-700">
                            Position: {patient.position?.title || "N/A"}
                          </p>

                        </div>

                        {/* EMERGENCY */}

                        <div className="bg-white rounded-lg border p-4 shadow-sm">

                          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">
                            Emergency Contact
                          </h4>

                          <p className="text-sm text-gray-700">
                            {patient.emergency_contact_name || "N/A"}
                          </p>

                          <p className="text-sm text-gray-700">
                            {patient.emergency_contact_phone || "N/A"}
                          </p>

                        </div>

                      </div>

                    </td>

                  </tr>

                )}

              </React.Fragment>

            ))}

          </tbody>

        </table>
      </div>

      {/* EMPTY STATE */}

      {patients.length === 0 && (

        <div className="text-center py-16">

          <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />

          <h3 className="text-lg font-medium text-gray-900 mb-1">
            No patients found
          </h3>

          <p className="text-gray-500">
            Try adjusting your search or add a new patient.
          </p>

        </div>

      )}

    </div>
  );
};

export default PatientTable;